import { Checkbox, FormControlLabel, Grid, TextField } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik, FormikErrors } from 'formik';
import { DraggableItem, ReviewsItem } from './DraggableItems';
import ConfirmationDialog from '../../../common/ui/ConfimationDialog';
import { ReviewsConfigData } from '../../../api';

export const REVIEW_PLATFORM_ICONS = {
  google_maps: 'https://upcdn.io/12a1z5L/image/uploads/logos/gmaps.png',
  yelp: 'https://upcdn.io/12a1z5L/image/uploads/logos/yelp-37CX.png',
  google_forms: 'https://upcdn.io/12a1z5L/image/uploads/logos/googleforms.png',
  trip_advisor: 'https://upcdn.io/12a1z5L/image/uploads/logos/tripadvisor-46uX.png',
} as const;

type FormValues = {
  reviewsConfig: ReviewsConfigData;
};
type ReviewConfigError = {
  url?: string;
};

type ReviewConfigErrors = {
  [K in keyof ReviewsConfigData]?: ReviewConfigError;
};
type EditReviewsDialogProps = {
  selectedItem?: DraggableItem;
  setSelectedItem: (item: DraggableItem) => void;
  isEditItemDialogOpen: boolean;
  setIsEditItemDialogOpen: (isOpen: boolean) => void;
  updateItem: (item: DraggableItem) => void;
};
export const EditReviewsDialog = ({
  selectedItem,
  setSelectedItem,
  isEditItemDialogOpen,
  setIsEditItemDialogOpen,
  updateItem,
}: EditReviewsDialogProps) => {
  const { t } = useTranslation();
  const initialValues: FormValues = {
    reviewsConfig: (selectedItem as ReviewsItem)?.reviewsConfig || {},
  };
  const validateForm = (values: FormValues): FormikErrors<FormValues> => {
    const errors: FormikErrors<FormValues> = {};
    const nestedErrors: ReviewConfigErrors = {};

    Object.entries(values.reviewsConfig || {}).forEach(([platform, config]) => {
      if (config?.enabled && (!config.url || config.url.trim() === '')) {
        nestedErrors[platform as keyof ReviewsConfigData] = {
          url: t('emailTemplates.urlRequired'),
        };
      }
    });

    if (Object.keys(nestedErrors).length > 0) {
      errors.reviewsConfig = nestedErrors as FormikErrors<ReviewsConfigData>;
    }

    return errors;
  };
  const handleConfigChange = (values: FormValues) => {
    const currentConfig = (selectedItem as ReviewsItem).reviewsConfig || {};
    const updatedConfig: ReviewsConfigData = {
      ...currentConfig,
      ...values.reviewsConfig,
    };
    const newItem = {
      ...selectedItem,
      reviewsConfig: updatedConfig,
    };
    setSelectedItem(newItem as ReviewsItem);
    updateItem(newItem as ReviewsItem);
  };
  return (
    <Formik initialValues={initialValues} validate={validateForm} onSubmit={() => {}}>
      {({ values, setFieldValue, errors, isValid }) => (
        <ConfirmationDialog
          title={t('emailTemplates.editReviewsDialog.title')}
          content={
            <Form>
              <Grid container flexDirection="column" gap={2} sx={{ minWidth: '400px', pt: 2 }}>
                {Object.entries(REVIEW_PLATFORM_ICONS).map(([platformStr, iconUrl]) => {
                  const platform = platformStr as keyof typeof REVIEW_PLATFORM_ICONS;
                  return (
                    <Grid item key={platform} container alignItems="center" mb={2}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.reviewsConfig[platform]?.enabled || false}
                              onChange={async (e) => {
                                await setFieldValue(`reviewsConfig.${platform}`, {
                                  ...values.reviewsConfig[platform],
                                  enabled: e.target.checked,
                                });
                              }}
                            />
                          }
                          label={
                            <Grid container alignItems="center" gap={1}>
                              <img src={iconUrl} alt={platform} style={{ width: 24, height: 24 }} />
                              {t(`emailTemplates.${platform}`)}
                            </Grid>
                          }
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t('emailTemplates.reviewUrl')}
                          value={values.reviewsConfig[platform]?.url || ''}
                          onChange={async (e) => {
                            await setFieldValue(`reviewsConfig.${platform}.url`, e.target.value);
                          }}
                          error={Boolean((errors.reviewsConfig as ReviewConfigErrors)?.[platform]?.url)}
                          helperText={(errors.reviewsConfig as ReviewConfigErrors)?.[platform]?.url || ''}
                        />
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            </Form>
          }
          isOpen={isEditItemDialogOpen}
          ctaText={t('save')}
          confirmButtonColor="primary"
          onClose={(actionConfirmed) => {
            if (actionConfirmed) {
              if (!isValid) {
                return;
              }
              setIsEditItemDialogOpen(false);
              handleConfigChange(values);
            } else {
              setIsEditItemDialogOpen(false);
            }
          }}
        />
      )}
    </Formik>
  );
};
